import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";

type UserAvatarProps = {
  displayName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
};

const sizeClass = {
  sm: "h-7 w-7 text-[10px]",
  md: "h-8 w-8 text-xs",
  lg: "h-16 w-16 text-lg",
} as const;

function initialsFor(
  displayName?: string | null,
  email?: string | null,
): string {
  const name = displayName?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const local = email?.trim().split("@")[0];
  if (local) return local.slice(0, 2).toUpperCase();
  return "?";
}

export function UserAvatar({
  displayName,
  email,
  avatarUrl,
  size = "md",
  className,
}: UserAvatarProps) {
  const [failed, setFailed] = useState(false);
  const initials = initialsFor(displayName, email);
  const showImage = Boolean(avatarUrl) && !failed;

  useEffect(() => {
    setFailed(false);
  }, [avatarUrl]);

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-rs-accent-soft font-semibold text-rs-accent-ink ring-1 ring-rs-border",
        sizeClass[size],
        className,
      )}
      aria-hidden
    >
      <span className="select-none">{initials}</span>
      {showImage ? (
        <img
          src={avatarUrl!}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : null}
    </span>
  );
}
